<template>
  <article
    class="product-mini-card"
    :class="{
      'product-mini-card--compact': compact,
      'product-mini-card--plain-action': !visibleRentalSchemes.length
    }"
    role="button"
    tabindex="0"
    :aria-label="normalizedProduct.name"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
  >
    <div class="product-mini-card__image-wrap">
      <img
        v-if="normalizedProduct.imgUrl && !imageFailed"
        class="product-mini-card__image"
        v-lazy="normalizedProduct.imgUrl"
        :alt="normalizedProduct.name"
        @load="$emit('image-load')"
        @error="handleImageError"
      >
      <span v-else class="product-mini-card__image-placeholder" aria-hidden="true"></span>
    </div>

    <div class="product-mini-card__name">{{ normalizedProduct.name }}</div>
    <div class="product-mini-card__price-label">{{ $t('productCommon.firstRentalAmount') }}</div>
    <div
      class="product-mini-card__price"
      :class="{ 'product-mini-card__price--pending': !hasPrice }"
    >
      {{ priceText }}
    </div>

    <div v-if="visibleRentalSchemes.length" class="product-mini-card__schemes">
      <span
        v-for="scheme in visibleRentalSchemes"
        :key="scheme.code || scheme.name"
        class="product-mini-card__scheme"
        :style="softColorStyle(scheme.color, '#2563EB', 0.12)"
      >
        {{ scheme.name }}
      </span>
    </div>

    <button class="product-mini-card__apply" type="button" @click.stop="handleClick">
      {{ $t('productCommon.applyNow') }}
    </button>
  </article>
</template>

<script>
import { getProductPriceText, normalizeProduct } from '@/common/product';

export default {
  name: 'HomeProductMiniCard',
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    showRentalSchemes: {
      type: Boolean,
      default: true
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageFailed: false
    };
  },
  computed: {
    normalizedProduct() {
      return normalizeProduct(this.product);
    },
    priceText() {
      return getProductPriceText(this.normalizedProduct, this.$t('productCommon.pricePending'));
    },
    hasPrice() {
      return this.normalizedProduct.price > 0;
    },
    imageUrl() {
      return this.normalizedProduct.imgUrl;
    },
    visibleRentalSchemes() {
      if (!this.showRentalSchemes) return [];
      return (this.normalizedProduct.rentalSchemes || []).slice(0, 3);
    }
  },
  watch: {
    imageUrl() {
      this.imageFailed = false;
    }
  },
  methods: {
    softColorStyle(color, fallbackColor, opacity) {
      const normalized = /^#[0-9A-Fa-f]{6}$/.test(color || '') ? color : fallbackColor;
      const red = parseInt(normalized.slice(1, 3), 16);
      const green = parseInt(normalized.slice(3, 5), 16);
      const blue = parseInt(normalized.slice(5, 7), 16);
      return {
        color: normalized,
        backgroundColor: `rgba(${red}, ${green}, ${blue}, ${opacity})`
      };
    },
    handleClick() {
      this.$emit('click', this.normalizedProduct);
    },
    handleImageError() {
      this.imageFailed = true;
      this.$emit('image-load');
    }
  }
};
</script>

<style lang="less" scoped>
.product-mini-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  padding: 16px;
  overflow: hidden;
  background: #FFFFFF;
  border: 1px solid #E5EAF3;
  border-radius: 20px;
  color: #070D3F;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid rgba(11, 69, 255, 0.32);
    outline-offset: 2px;
  }

  &--compact &__image-wrap {
    height: 228px;
  }

  &__image-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 260px;
    overflow: hidden;
    background: #FFFFFF;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__image-placeholder {
    position: relative;
    width: 92px;
    height: 82px;
    border: 5px solid #D8E2F2;
    border-radius: 12px;

    &::before {
      content: '';
      position: absolute;
      top: -28px;
      left: 24px;
      width: 34px;
      height: 30px;
      border: 5px solid #D8E2F2;
      border-bottom: 0;
      border-radius: 20px 20px 0 0;
    }
  }

  &__name {
    margin-top: 8px;
    overflow: hidden;
    color: #070D3F;
    font-size: 26px;
    font-weight: 600;
    line-height: 36px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__price-label {
    margin-top: 16px;
    overflow: hidden;
    color: #6B7280;
    font-size: 24px;
    line-height: 32px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__price {
    min-height: 42px;
    overflow: hidden;
    color: #0B45FF;
    font-size: 32px;
    font-weight: 500;
    line-height: 42px;
    text-overflow: clip;
    white-space: nowrap;

    &--pending {
      font-size: 26px;
    }
  }

  &--plain-action &__price {
    margin-bottom: 18px;
  }

  &__schemes {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
    margin-bottom: 16px;
    overflow: hidden;
  }

  &__scheme {
    box-sizing: border-box;
    max-width: 100%;
    min-height: 28px;
    padding: 4px 12px;
    overflow: hidden;
    border-radius: 8px;
    font-size: 20px;
    font-weight: 500;
    line-height: 26px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__apply {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 80px;
    min-height: 80px;
    margin-top: auto;
    padding: 0 12px;
    overflow: hidden;
    background: linear-gradient(90deg, #2A5BFF 0%, #0B45FF 100%);
    border: 0;
    border-radius: 20px;
    color: #FFFFFF;
    font-family: inherit;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }
}

@media (max-width: 340px) {
  .product-mini-card {
    padding: 12px;

    &__image-wrap {
      height: 234px;
    }

    &--compact &__image-wrap {
      height: 210px;
    }

    &__schemes {
      gap: 6px;
    }

    &__scheme {
      font-size: 18px;
    }
  }
}
</style>
